import { MenuRow, MenuSurface } from '~/design-system/components/v1_5/Header';
import type { DirectoryKind, DirectoryRowAction, DirectoryView, HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/** All three of the Directory's menus are this wide. They are read as one family, as the Header's four are. */
export const DIRECTORY_MENU_WIDTH = 196;

/**
 * The four kinds, in their fixed order, with the words and marks the whole panel uses for them.
 *
 * They live here because this is the one place all four are enumerated as a set — the header names
 * one, the empty state names one, this menu names all of them. Two hand-written lists of four kinds
 * is how the header ends up saying "Websites" over a globe that has quietly become something else.
 */
export const KIND_LABEL: Record<DirectoryKind, string> = {
  artifacts: 'Artifacts',
  documents: 'Documents',
  websites: 'Websites',
  images: 'Images',
};

/** The kind marks. Also the fallback glyph for a row's leading tile and the empty state's illustration. */
export const KIND_ICON: Record<DirectoryKind, string> = {
  artifacts: 'slideshow-view',
  documents: 'file-text',
  websites: 'global',
  images: 'image',
};

/**
 * What a kind's `⋯` menu offers, in menu order, delete last. The one authored copy — the Directory
 * row and the Preview bar both render `RowMenu` off this, so "the same three dots" stays literally
 * true. These are the kind's SUPERSET: the host subtracts per item (`undo-edit` only with a held
 * previous source, `extract-styles` never on a social post) and every row it leaves in is wired.
 */
export const KIND_ROW_ACTIONS: Record<DirectoryKind, DirectoryRowAction[]> = {
  // Undo/redo left the menu for the preview bar's own icon pair, and Artifact theme is parked until
  // its real home is decided (Guri, 2026-08-30) — both actions still exist, neither is a menu row.
  artifacts: ['open', 'rename', 'present', 'pin-reference', 'download', 'duplicate', 'delete'],
  documents: ['open', 'rename', 'download', 'duplicate', 'delete'],
  websites: ['open', 'rename', 'extract-text', 'extract-images', 'extract-styles', 'copy-url', 'refetch', 'duplicate', 'delete'],
  // Add to artifact places the picture on the open artifact, on top and free to move (Beka, 2026-09-25).
  images: ['open', 'add-to-artifact', 'rename', 'download', 'duplicate', 'delete'],
};

/**
 * The VIEWS the switcher offers beyond the kinds, with their words and marks. Separate records
 * rather than widened KIND_* ones, so nothing that iterates the kinds accidentally gains rows;
 * `VIEW_LABEL`/`VIEW_ICON` below are the union the header and this menu read.
 */
export const VIEW_ONLY_LABEL: Record<'all' | 'other', string> = {
  all: 'All sources',
  // "Other" against the four kinds above it, which are the deck's OWN sources. This view is
  // everything else: what Figma, Linear, GitHub or Notion holds, reached through a connection
  // rather than stored in the deck.
  other: 'Other sources',
};

export const VIEW_ONLY_ICON: Record<'all' | 'other', string> = {
  // A stand-in until Beka draws a real mark: the eye reads as "everything on view".
  all: 'eye',
  // `integrations-2`, NOT `Integrations`. Both are in the registry, they are different drawings,
  // and the capital-I one is the profile menu's nav glyph. This is the same mark Billing already
  // prints beside "Connect Figma, Linear, GitHub and Notion", which is exactly this view's subject.
  other: 'integrations-2',
};

export const VIEW_LABEL: Record<DirectoryView, string> = { ...KIND_LABEL, ...VIEW_ONLY_LABEL };
export const VIEW_ICON: Record<DirectoryView, string> = { ...KIND_ICON, ...VIEW_ONLY_ICON };

/**
 * No shortcut hints on these rows.
 *
 * Paper prints ⌘1–⌘4 here, but nothing switches kind on a key in this build, and nothing may bind
 * ⌘1–⌘9: the browser owns them for switching tabs. Header rule 5: no control for
 * functionality that does not exist, and a printed shortcut is a control. If kind-switching keys
 * are ever bound, they must not be ⌘ and a number.
 *
 * `All sources` leads — it is the blend the four kinds are cuts of — and `Other sources` closes the
 * list, because it is the one entry that is not in the blend at all: everything above it is what
 * this deck holds, and it is what somebody else's app holds.
 */
const ORDER: DirectoryView[] = ['all', 'artifacts', 'documents', 'websites', 'images', 'other'];

export interface KindMenuProps {
  /** The view on screen. It is the `selected` row, the same fill a selected list row carries. */
  kind: DirectoryView;
  onPick?: (view: DirectoryView) => void;
  theme?: HeaderTheme;
  className?: string;
}

/**
 * The kind switcher, off the header's caret.
 *
 * The caret is the only way between the four — the panel shows one kind at a time and there is no
 * tab strip, no segment and no rail — so this menu is not optional chrome. Without it the caret is
 * a control for nothing and three of the four kinds are unreachable.
 */
export function KindMenu({ kind, onPick, theme: themeProp, className }: KindMenuProps) {
  const theme = useV15Theme(themeProp);
  return (
    <MenuSurface
      width={DIRECTORY_MENU_WIDTH}
      theme={theme}
      label="Kind"
      className={className}
    >
      {ORDER.map((id) => (
        <MenuRow
          key={id}
          icon={VIEW_ICON[id]}
          label={VIEW_LABEL[id]}
          selected={id === kind}
          theme={theme}
          onClick={() => onPick?.(id)}
        />
      ))}
    </MenuSurface>
  );
}
